import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '../common/Divider';
import { OperationSettlementStatusBarIcon } from './OperationSettlementStatusBarIcon';
import { OperationSettlementStatus } from './OperationSettlementStatusBar';

interface OperationSettlementTableProps {
  data: Array<SettlementTableData>;
  testId?: string;
  className?: string;
  onClick?: (data: { title: string }) => void;
}

export const OperationSettlementTable: React.FC<
  OperationSettlementTableProps
> = ({ data, testId, className, onClick }) => {
  const { t } = useTranslation();

  const headers = [
    t('settlement.resourceName'),
    t('settlement.generatorName'),
    t('settlement.contracteeType'),
    t('settlement.earnings'),
    t('settlement.totalGeneration'),
    t('settlement.generationRatio'),
    t('settlement.resourceSettlement'),
    t('settlement.plantIncentive'),
    t('settlement.currentStatus'),
  ];
  return (
    <table data-testid={testId} className={`${className} w-full`}>
      <thead>
        <tr className="h-[36px] rounded-[5px] bg-dark-4 text-white">
          {headers.map((value, index) =>
            index === 0 ? (
              <th
                key={index}
                className="pop-normal-3 rounded-bl-[5px] rounded-tl-[5px]"
              >
                {value}
              </th>
            ) : index === headers.length - 1 ? (
              <th
                key={index}
                className="pop-normal-3 rounded-br-[5px] rounded-tr-[5px]"
              >
                {value}
              </th>
            ) : (
              <th key={index} className="pop-normal-3">
                {value}
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody>
        {data.map((value, index) => (
          <tr key={index} data-testid={`${testId}-item`}>
            {Object.values(value).map((value, index, array) => {
              const generatorNameIndex = 1;
              const lastIndex = array.length - 1;

              if (generatorNameIndex === index)
                return (
                  <td
                    onClick={() => onClick?.({ title: value })}
                    data-testid={`${testId}-item-title`}
                    className="noto-normal-3 cursor-pointer border-b border-[#E2E7E7] py-[13px] text-center leading-none tracking-[-0.5px] text-blue-main underline decoration-blue-main underline-offset-2"
                    key={index}
                  >
                    {value}
                  </td>
                );
              else if (lastIndex === index)
                return (
                  <td
                    key={index}
                    className="flex items-center justify-center border-b border-[#E2E7E7] py-[13px]"
                  >
                    {Object.values(OperationSettlementStatus).map(
                      (_, index, array) => {
                        const isLast = index === array.length - 1;
                        const isActive = value === index + 1;

                        return (
                          <div key={index} className="flex items-center">
                            <OperationSettlementStatusBarIcon
                              className={`noto-11-semibold h-6 w-6 ${isActive ? 'bg-teal-light' : ''}`}
                              text={(index + 1).toString().padStart(2, '0')}
                            />
                            {!isLast && (
                              <Divider
                                className={`h-[2px] w-[14px] bg-light-6 ${isActive ? 'bg-teal-light' : ''}`}
                              />
                            )}
                          </div>
                        );
                      }
                    )}
                  </td>
                );
              else
                return (
                  <td
                    className="noto-normal-3 border-b border-[#E2E7E7] py-[13px] text-center leading-none tracking-[-0.5px] text-dark-6"
                    key={index}
                  >
                    {value}
                  </td>
                );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
